/**
 * Canonical option lists for the SP Firm Profile multi-selects
 * (industriesOfFocus / languages) — single source of truth for the client
 * (checkbox options, firm-list filter, display mapping) and the server
 * (write-path validation in provider.schemas.ts / firmProfile.ts). Review R21.
 *
 * Storage shape across the stack is a single "; "-delimited string. Legacy
 * records predate the fixed lists and hold arbitrary free text (often
 * comma-separated, e.g. "SaaS, Hardware, Biotech"); tokenizeOptionList
 * preserves those tokens verbatim instead of collapsing them to "Other"
 * (review R18 — legacy data must survive a round-trip through the form).
 *
 * The client keeps the display metadata (frText) in
 * eip-client/src/utilities/industryOptions.ts / languageOptions.ts, both of
 * which are thin instantiations of makeOptionHelpers below.
 */

export const FIRM_OPTIONS_DELIMITER = "; ";

/**
 * Upper bound on the whole stored option string. This is a DoS/abuse bound on
 * payload size, NOT a business rule — the business-level token validation
 * ("canonical or already stored on this firm") lives in the controller
 * (firmProfile.ts). It is deliberately generous (all 14 canonical industries
 * joined ≈ 800 chars) so that long legacy free text PLUS a full canonical
 * selection can never make a profile unsavable (R18): zod enforces this cap
 * BEFORE the controller's stored-token allowance runs, so a tight cap here
 * would reject legitimate legacy-preserving round-trips.
 */
export const FIRM_OPTION_FIELD_MAX_LENGTH = 10000;

// Mirrors the founder application "Industry" question (founderProfileBase.ts)
// so firm and founder industry values stay consistent. NOTE (R22, backlogged):
// the founder-side copies do not consume this list yet — keep them in sync.
export const FIRM_INDUSTRY_VALUES: readonly string[] = [
  "Agriculture",
  "Advanced Materials & Manufacturing: Excludes specifically forestry, agriculture, mining, clean technologies and life sciences",
  "Clean Tech: Technologies that protect and/or increase efficient utilization of land, water and air resources",
  "Consumer Retail",
  "Digital Media & Telecommunications: Digital applications and content and information and communications technologies. Excludes healthcare, agriculture, forestry, mining, financial services, education, social innovation, culture/recreation and energy-related solutions",
  "Education",
  "Financial Services",
  "Food & Beverage",
  "Forestry",
  "Life Sciences & Health",
  "Mining, Oil & Gas: Includes extractive activities that would not otherwise fall under clean technologies",
  "Tourism & Culture: Includes entertainment and sports",
  "Transportation",
  "Other",
];

// Popular languages offered by SP firms, Canadian-context. "Chinese" is split
// into its two common spoken variants (Mandarin/Cantonese).
export const FIRM_LANGUAGE_VALUES: readonly string[] = [
  "English",
  "French",
  "Spanish",
  "Mandarin",
  "Cantonese",
  "Punjabi",
  "Hindi",
  "Urdu",
  "Arabic",
  "Tagalog",
  "Portuguese",
  "German",
  "Italian",
  "Other",
];

const lowerCaseLookup = (canonical: readonly string[]): Map<string, string> => {
  const byLower = new Map<string, string>();
  for (const v of canonical) byLower.set(v.toLowerCase(), v);
  return byLower;
};

// Hoisted lookups for the two fixed lists (C23): tokenizeOptionList is called
// per firm per keystroke in the founder firm-list filter, so the lowercase Map
// must not be rebuilt on every call. Arbitrary (test-supplied) lists still get
// a fresh lookup.
const INDUSTRY_LOOKUP = lowerCaseLookup(FIRM_INDUSTRY_VALUES);
const LANGUAGE_LOOKUP = lowerCaseLookup(FIRM_LANGUAGE_VALUES);

const lookupFor = (canonical: readonly string[]): Map<string, string> => {
  if (canonical === FIRM_INDUSTRY_VALUES) return INDUSTRY_LOOKUP;
  if (canonical === FIRM_LANGUAGE_VALUES) return LANGUAGE_LOOKUP;
  return lowerCaseLookup(canonical);
};

/**
 * Splits a stored option string into clean tokens (review R18).
 *
 * - Primary delimiter is ";" (the canonical "; " form, tolerant of spacing).
 * - Case-insensitive canonical matching: "english" resolves to "English".
 *   Canonical values containing commas ("Mining, Oil & Gas: …") are matched
 *   whole against each ";"-part first, so a standalone comma-bearing
 *   canonical is never broken apart.
 * - Conservative comma fallback for legacy data (C9): a ";"-part that does
 *   NOT match a canonical value is comma-split ONLY when EVERY comma-separated
 *   fragment (trimmed, case-insensitive) is itself canonical
 *   ("English, French" → two canonical tokens). Otherwise the whole part is
 *   preserved verbatim as ONE token — comma-bearing free text like
 *   "Health, Wellness & Fitness" is no longer shredded into fragments.
 *   Known residual (accepted): a comma-bearing canonical embedded in a
 *   comma-joined legacy string alongside free text ("SaaS, Mining, Oil &
 *   Gas: …") stays one verbatim token rather than being extracted — the data
 *   is preserved intact, just not canonicalized.
 * - Unmatched tokens are PRESERVED verbatim — never collapsed to "Other".
 * - De-duplicated case-insensitively, original order kept.
 */
export function tokenizeOptionList(
  value: string | undefined | null,
  canonical: readonly string[]
): string[] {
  if (!value) return [];
  const byLower = lookupFor(canonical);
  const out: string[] = [];
  const seen = new Set<string>();

  const push = (raw: string) => {
    const token = raw.trim();
    if (!token) return;
    const resolved = byLower.get(token.toLowerCase()) ?? token;
    const key = resolved.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    out.push(resolved);
  };

  for (const part of value.split(";")) {
    const token = part.trim();
    if (!token) continue;
    if (byLower.has(token.toLowerCase())) {
      push(token);
      continue;
    }
    const fragments = token
      .split(",")
      .map((f) => f.trim())
      .filter((f) => f.length > 0);
    if (fragments.length === 0) continue;
    const allCanonical = fragments.every((f) => byLower.has(f.toLowerCase()));
    if (allCanonical) {
      for (const fragment of fragments) push(fragment);
    } else {
      push(token);
    }
  }
  return out;
}

/** Serializes tokens back to the canonical stored form. */
export function serializeOptionList(values: string[]): string {
  return values.join(FIRM_OPTIONS_DELIMITER);
}

/**
 * Server write-path guard (review R21). Returns the incoming tokens that are
 * neither canonical nor already present on the stored value — i.e. brand-new
 * free text. Tokens already stored on THIS document are allowed through so
 * the client's legacy-preserving round-trip (R18) is never rejected, while
 * stale clients / direct API callers cannot introduce new free text.
 */
export function findDisallowedOptionTokens(
  incoming: string,
  stored: string | undefined | null,
  canonical: readonly string[]
): string[] {
  const canonicalSet = new Set(canonical.map((v) => v.toLowerCase()));
  const storedTokens = new Set(
    tokenizeOptionList(stored, canonical).map((t) => t.toLowerCase())
  );
  return tokenizeOptionList(incoming, canonical).filter((token) => {
    const key = token.toLowerCase();
    return !canonicalSet.has(key) && !storedTokens.has(key);
  });
}

// ── Option-helper factory (C16/C31) ──────────────────────────────────
// industryOptions.ts and languageOptions.ts on the client are structural
// twins; this factory carries the shared shape so each client module is a
// thin instantiation supplying only its canonical list + French display text.

export interface OptionSelectionData {
  value: string;
  text: string;
  frText: string;
}

export interface OptionHelpers {
  /** SelectionData-shaped list for the multi-select (frText falls back to the value). */
  options: OptionSelectionData[];
  /** Tokenizes a stored string against the canonical list (tokenizeOptionList). */
  parse: (value?: string | null) => string[];
  /** Serializes tokens back to the "; "-delimited stored form. */
  serialize: (values: string[]) => string;
  /** Render-time display mapping (R20): FR mode maps through frText; legacy tokens render verbatim. */
  display: (token: string, lang: string) => string;
  /** Parses a stored string and joins the display-mapped tokens with the delimiter. */
  displayList: (value: string | undefined, lang: string) => string;
}

export function makeOptionHelpers(
  values: readonly string[],
  frText: Record<string, string>
): OptionHelpers {
  const options: OptionSelectionData[] = values.map((value) => ({
    value,
    text: value,
    frText: frText[value] ?? value,
  }));
  const parse = (value?: string | null): string[] => tokenizeOptionList(value, values);
  const serialize = (tokens: string[]): string => serializeOptionList(tokens);
  const display = (token: string, lang: string): string => {
    if (lang !== "fr") return token;
    return frText[token] ?? token;
  };
  const displayList = (value: string | undefined, lang: string): string =>
    parse(value)
      .map((token) => display(token, lang))
      .join(FIRM_OPTIONS_DELIMITER);
  return { options, parse, serialize, display, displayList };
}
